import { Navigate, RouteObject, createBrowserRouter } from 'react-router-dom';
import WrapperRouteComponent from './config';
import Middleware from '@/middleware';
import KYCMiddleware from '@/middleware/kyc.middleware';
import { PAGE_TITLES } from '@/constants/titles';
import BaseLayout from '@/layouts/base.layout';
import MainLayout from '@/layouts/main.layout';
import AuthLayout from '@/layouts/auth.layout';
import KYCLayout from '@/layouts/kyc.layout';
import { AuthWrapper } from '@/components/HOC/AuthWrapper';
import { lazyRetry } from '@/utils/lazy-retry';
import InviteLayout from '@/layouts/invite.layout';
import TransferLayout from '@/modules/virtual-account/layouts/transfer.layout';

// Lazy load các components
//module

//Main menu

const DashboardPage = lazyRetry(
  () => import(/* webpackChunkName: "Dashboard"*/ '@/modules/dashboard'),
);

const VirtualAccountPage = lazyRetry(
  () => import(/* webpackChunkName: "virtual-account"*/ '@/modules/virtual-account'),
);

const CardPage = lazyRetry(() => import(/* webpackChunkName: "card"*/ '@/modules/card'));

const TransactionPage = lazyRetry(
  () => import(/* webpackChunkName: "transaction"*/ '@/modules/transaction'),
);

const CustomerRequestPage = lazyRetry(
  () => import(/* webpackChunkName: "customer-request"*/ '@/modules/customer-request'),
);

const CashbackDetail = lazyRetry(
  () => import(/* webpackChunkName: "virtual-account-transfer"*/ '@/modules/cashback/detail'),
);

const CashbackPage = lazyRetry(
  () => import(/* webpackChunkName: "cashback"*/ '@/modules/cashback/index'),
)
const StableCoinPage = lazyRetry(
  () => import(/* webpackChunkName: "stablecoin"*/ '@/modules/stableCoin'),
)
const SettingCompanyProfile = lazyRetry(
  () =>
    import(/* webpackChunkName: "setting-company-profile"*/ '@/modules/settings/company/profile'),
);

const SettingCompanySecurity = lazyRetry(
  () =>
    import(/* webpackChunkName: "setting-company-security"*/ '@/modules/settings/company/security'),
);

// Other Menu

const ProfilePage = lazyRetry(
  () => import(/* webpackChunkName: "settings"*/ '@/modules/settings/profile'),
);

const BalanceAlertRulesPage = lazyRetry(
  () => import(/* webpackChunkName: "settings"*/ '@/modules/settings/balance-alert-rules'),
);

const BotPage = lazyRetry(
  () => import(/* webpackChunkName: "settings"*/ '@/modules/settings/bot'),
);

const TransactionNotificationRulePage = lazyRetry(
  () => import(/* webpackChunkName: "settings"*/ '@/modules/settings/transaction-notification-rules'),
);

const UserPage = lazyRetry(
  () => import(/* webpackChunkName: "user-detail"*/ '@/modules/settings/user'),
);

const UserDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "user-detail"*/ '@/modules/settings/user/user-detail'),
);

const AddUserPage = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "virtual-account-transfer"*/ '@/modules/settings/user/addUser'
    ),
);

//Auth
const AuthPage = lazyRetry(() => import(/* webpackChunkName: "login"*/ '@/pages/Auth'));

//Invite
const InvitePage = lazyRetry(() => import(/* webpackChunkName: "invite"*/ '@/pages/Invite'));
//Transfer
const TransferSelectionPage = lazyRetry(
  () =>
    import(
      /* webpackChunkName: "virtual-account-transfer-selection"*/ '@/pages/Transfer/TransferSelection'
    ),
);
const InternalTransferPage = lazyRetry(
  () =>
    import(/* webpackChunkName: "virtual-account-transfer"*/ '@/pages/Transfer/InternalTransfer'),
);

const USDTTransferPage = lazyRetry(
  () => import(/* webpackChunkName: "virtual-account-transfer"*/ '@/pages/Transfer/USDTTransfer'),
);

//KYC Pages
const KYCSubmitPage = lazyRetry(() => import('@/pages/KYC/submit'));
const KYCPendingPage = lazyRetry(() => import('@/pages/KYC/pending'));
const KYCResubmitPage = lazyRetry(() => import('@/pages/KYC/resubmit'));
const KYCVerifiedPage = lazyRetry(() => import('@/pages/KYC/verified'));

//not-found
const NotFoundPage = lazyRetry(() => import('@/pages/NotFound'));

// Định nghĩa các routes
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <KYCMiddleware>
            <WrapperRouteComponent title="">
              <MainLayout />
            </WrapperRouteComponent>
          </KYCMiddleware>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: '/dashboard',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.DASHBOARD}>
            <DashboardPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/virtual-account',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.VirtualAccount}>
            <VirtualAccountPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/card',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CARD}>
            <CardPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/customer-request',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CustomerRequest}>
            <CustomerRequestPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/transaction',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.TRANSACTION}>
            <TransactionPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/cashback/:id',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CASHBACK}>
            <CashbackDetail />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/settings/user/:id',
        element: <UserDetailPage />,
      },
      {
        path: '/settings/add-user',
        element: <AddUserPage />,
      },
      {
        path: '/cashback',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CASHBACK}>
            <CashbackPage />
          </WrapperRouteComponent>
        )
      },
      {
        path: '/stablecoin-payments',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.STABLECOIN}>
            <StableCoinPage />
          </WrapperRouteComponent>
        )
      },
      {
        path: '/settings',
        children: [
          {
            path: 'users',
            element: (
              <WrapperRouteComponent title={PAGE_TITLES.SETTINGS_USERS}>
                <UserPage />
              </WrapperRouteComponent>
            ),
          },
          {
            path: 'company',
            children: [
              {
                path: 'profile',
                element: (
                  <WrapperRouteComponent title={PAGE_TITLES.SETTINGSCOMPANYPROFILE}>
                    <SettingCompanyProfile />
                  </WrapperRouteComponent>
                ),
              },
              {
                path: 'security',
                element: (
                  <WrapperRouteComponent title={PAGE_TITLES.SETTINGSCOMPANYSECURITY}>
                    <SettingCompanySecurity />
                  </WrapperRouteComponent>
                ),
              },
              {
                path: 'personalprofile',
                element: (
                  <WrapperRouteComponent title={PAGE_TITLES.SETTINGS_PROFILE}>
                    <ProfilePage />
                  </WrapperRouteComponent>
                ),
              },
            ],
          },
          {
            path: 'personal',
            children: [
              {
                path: 'profile',
                element: (
                  <WrapperRouteComponent title={PAGE_TITLES.SETTINGS_PROFILE}>
                    <ProfilePage />
                  </WrapperRouteComponent>
                ),
              }
            ],
          },
          {
            path: 'Notification',
            children: [
              {
                path: 'balance-alert-rules',
                element: (
                  <WrapperRouteComponent title={PAGE_TITLES.SETTINGS_BALANCE_ALERT_RULES}>
                    <BalanceAlertRulesPage />
                  </WrapperRouteComponent>
                ),
              },
              {
                path: 'bot',
                element: (
                  <WrapperRouteComponent title={PAGE_TITLES.SETTINGS_BOT}>
                    <BotPage />
                  </WrapperRouteComponent>
                ),
              },
              {
                path: 'transaction-rules',
                element: (
                  <WrapperRouteComponent title={PAGE_TITLES.SETTINGS_TRANSACTION_RULES}>
                    <TransactionNotificationRulePage />
                  </WrapperRouteComponent>
                ),
              }
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/',
    element: (
      <WrapperRouteComponent title="">
        <Middleware mode="non-login">
          <AuthLayout />
        </Middleware>
      </WrapperRouteComponent>
    ),
    children: [
      {
        path: 'login',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'register',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'verify',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <AuthPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/invite/:inviteCode',
    element: (
      <WrapperRouteComponent title="">
        <Middleware mode="non-login">
          <InviteLayout />
        </Middleware>
      </WrapperRouteComponent>
    ),
    children: [
      {
        index: true,
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <InvitePage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'register',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <InvitePage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <KYCMiddleware>
            <WrapperRouteComponent title="">
              <BaseLayout />
            </WrapperRouteComponent>
          </KYCMiddleware>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        path: '/virtual-account/transfer',
        element: (
          <WrapperRouteComponent title="Transfer Fund">
            <TransferLayout />
          </WrapperRouteComponent>
        ),
        children: [
          {
            index: true,
            element: (
              <WrapperRouteComponent title="Transfer Fund">
                <TransferSelectionPage />
              </WrapperRouteComponent>
            ),
          },
          {
            path: 'internal-transfer',
            element: (
              <WrapperRouteComponent title="Transfer Fund">
                <InternalTransferPage />
              </WrapperRouteComponent>
            ),
          },
          {
            path: 'usdc-usdt-transfer',
            element: (
              <WrapperRouteComponent title="Transfer Fund">
                <USDTTransferPage />
              </WrapperRouteComponent>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/kyc',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <KYCMiddleware>
            <WrapperRouteComponent title="">
              <KYCLayout />
            </WrapperRouteComponent>
          </KYCMiddleware>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        path: 'submit',
        element: (
          <WrapperRouteComponent title="KYC Submit">
            <KYCSubmitPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'pending',
        element: (
          <WrapperRouteComponent title="KYC Pending">
            <KYCPendingPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'resubmit',
        element: (
          <WrapperRouteComponent title="KYC Resubmit">
            <KYCResubmitPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: 'verified',
        element: (
          <WrapperRouteComponent title="KYC Verified">
            <KYCVerifiedPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/not-found',
    element: (
      <WrapperRouteComponent title={PAGE_TITLES.NOT_FOUND}>
        <NotFoundPage />
      </WrapperRouteComponent>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];

// Export router
export const router = createBrowserRouter(routes);
